import { useEffect, useState } from 'react';
import { reportsAPI, productLinesAPI } from '../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import {
  BarChart3, PieChart, TrendingUp, Users, Package, DollarSign, 
  Target, RefreshCw, ArrowUpRight, ArrowDownRight, Minus,
  Download, FileText, FileSpreadsheet
} from 'lucide-react';

export default function ReportsPage() {
  const [productLineAnalytics, setProductLineAnalytics] = useState(null);
  const [salesOverview, setSalesOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('product-lines');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [plRes, salesRes] = await Promise.all([
        reportsAPI.getProductLineAnalytics(),
        reportsAPI.getSalesOverview()
      ]);
      setProductLineAnalytics(plRes.data);
      setSalesOverview(salesRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getMaxCount = () => {
    if (!productLineAnalytics?.product_line_stats) return 1;
    return Math.max(...productLineAnalytics.product_line_stats.map(pl => pl.contact_count), 1);
  };

  // Export to CSV
  const exportToCSV = () => {
    let csvContent = '';
    const timestamp = new Date().toISOString().split('T')[0];

    if (activeTab === 'product-lines') {
      // Product Lines Summary
      csvContent += 'Product Lines Report\n';
      csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;
      
      csvContent += 'Summary\n';
      csvContent += `Total Contacts,${productLineAnalytics?.total_contacts || 0}\n`;
      csvContent += `Contacts With Product Line,${productLineAnalytics?.contacts_with_product_line || 0}\n`;
      csvContent += `Contacts Without Product Line,${productLineAnalytics?.contacts_without_product_line || 0}\n`;
      csvContent += `Multi-Product Customers,${productLineAnalytics?.multi_product_line_contacts || 0}\n\n`;

      // Product Line Distribution
      csvContent += 'Product Line Distribution\n';
      csvContent += 'Product Line,Contact Count\n';
      productLineAnalytics?.product_line_stats?.forEach(pl => {
        csvContent += `${pl.name},${pl.contact_count}\n`;
      });
      csvContent += '\n';

      // Monthly Trends
      if (productLineAnalytics?.monthly_trends?.length > 0) {
        csvContent += 'Monthly Trends\n';
        const productNames = productLineAnalytics?.product_line_stats?.map(pl => pl.name) || [];
        csvContent += `Month,${productNames.join(',')}\n`;
        productLineAnalytics.monthly_trends.forEach(month => {
          const values = productNames.map(name => month.product_lines[name] || 0);
          csvContent += `${month.month},${values.join(',')}\n`;
        });
      }
    } else {
      // Sales Overview
      csvContent += 'Sales Overview Report\n';
      csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;
      
      csvContent += 'Summary\n';
      csvContent += `Total Pipeline Value,$${salesOverview?.total_pipeline_value || 0}\n`;
      csvContent += `Closed Won Value,$${salesOverview?.closed_won_value || 0}\n`;
      csvContent += `Win Rate,${salesOverview?.win_rate || 0}%\n`;
      csvContent += `Total Deals,${salesOverview?.total_deals || 0}\n\n`;

      // Deal Pipeline
      csvContent += 'Deal Pipeline by Stage\n';
      csvContent += 'Stage,Count,Value\n';
      if (salesOverview?.deal_by_stage) {
        Object.entries(salesOverview.deal_by_stage).forEach(([stage, data]) => {
          csvContent += `${stage},${data.count},$${data.value}\n`;
        });
      }
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${activeTab}-report-${timestamp}.csv`);
    toast.success('CSV exported successfully');
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const timestamp = new Date().toISOString().split('T')[0];
    let yPos = 20;

    // Title
    doc.setFontSize(18);
    doc.setTextColor(31, 41, 55);
    
    if (activeTab === 'product-lines') {
      doc.text('Product Lines Report', 14, yPos);
      yPos += 10;
      
      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos);
      yPos += 15;

      // Summary Section
      doc.setFontSize(14);
      doc.setTextColor(31, 41, 55);
      doc.text('Summary', 14, yPos);
      yPos += 10;

      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: [
          ['Total Contacts', String(productLineAnalytics?.total_contacts || 0)],
          ['Contacts With Product Line', String(productLineAnalytics?.contacts_with_product_line || 0)],
          ['Contacts Without Product Line', String(productLineAnalytics?.contacts_without_product_line || 0)],
          ['Multi-Product Customers', String(productLineAnalytics?.multi_product_line_contacts || 0)],
        ],
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 14 },
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // Product Line Distribution
      doc.setFontSize(14);
      doc.text('Product Line Distribution', 14, yPos);
      yPos += 10;

      if (productLineAnalytics?.product_line_stats?.length > 0) {
        autoTable(doc, {
          startY: yPos,
          head: [['Product Line', 'Contact Count']],
          body: productLineAnalytics.product_line_stats.map(pl => [pl.name, String(pl.contact_count)]),
          theme: 'striped',
          headStyles: { fillColor: [16, 185, 129] },
          margin: { left: 14 },
        });
        yPos = doc.lastAutoTable.finalY + 15;
      }

      // Monthly Trends
      if (productLineAnalytics?.monthly_trends?.length > 0 && yPos < 240) {
        doc.setFontSize(14);
        doc.text('Monthly Trends', 14, yPos);
        yPos += 10;

        const productNames = productLineAnalytics?.product_line_stats?.map(pl => pl.name) || [];
        autoTable(doc, {
          startY: yPos,
          head: [['Month', ...productNames]],
          body: productLineAnalytics.monthly_trends.map(month => [
            month.month,
            ...productNames.map(name => String(month.product_lines[name] || 0))
          ]),
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246] },
          margin: { left: 14 },
        });
      }
    } else {
      doc.text('Sales Overview Report', 14, yPos);
      yPos += 10;
      
      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos);
      yPos += 15;

      // Summary Section
      doc.setFontSize(14);
      doc.setTextColor(31, 41, 55);
      doc.text('Summary', 14, yPos);
      yPos += 10;

      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: [
          ['Total Pipeline Value', formatCurrency(salesOverview?.total_pipeline_value || 0)],
          ['Closed Won Value', formatCurrency(salesOverview?.closed_won_value || 0)],
          ['Win Rate', `${salesOverview?.win_rate || 0}%`],
          ['Total Deals', String(salesOverview?.total_deals || 0)],
        ],
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 14 },
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // Deal Pipeline
      doc.setFontSize(14);
      doc.text('Deal Pipeline by Stage', 14, yPos);
      yPos += 10;

      if (salesOverview?.deal_by_stage) {
        const stageLabels = {
          prospecting: 'Prospecting',
          qualification: 'Qualification',
          proposal: 'Proposal',
          negotiation: 'Negotiation',
          closed_won: 'Won',
          closed_lost: 'Lost'
        };

        autoTable(doc, {
          startY: yPos,
          head: [['Stage', 'Count', 'Value']],
          body: Object.entries(salesOverview.deal_by_stage).map(([stage, data]) => [
            stageLabels[stage] || stage,
            String(data.count),
            formatCurrency(data.value)
          ]),
          theme: 'striped',
          headStyles: { fillColor: [16, 185, 129] },
          margin: { left: 14 },
        });
      }
    }

    doc.save(`${activeTab}-report-${timestamp}.pdf`);
    toast.success('PDF exported successfully');
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-200 rounded-lg"></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reports & Analytics</h1>
          <p className="text-muted-foreground">Insights into your business performance</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportToCSV} data-testid="export-csv-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={exportToPDF} data-testid="export-pdf-btn">
            <FileText className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button variant="outline" onClick={fetchReports}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="product-lines" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="product-lines" data-testid="tab-product-lines">
            <Package className="h-4 w-4 mr-2" />
            Product Lines
          </TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales">
            <DollarSign className="h-4 w-4 mr-2" />
            Sales Overview
          </TabsTrigger>
        </TabsList>

        {/* Product Lines Tab */}
        <TabsContent value="product-lines" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Contacts</p>
                    <p className="text-2xl font-bold">{productLineAnalytics?.total_contacts || 0}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">With Product Line</p>
                    <p className="text-2xl font-bold">{productLineAnalytics?.contacts_with_product_line || 0}</p>
                  </div>
                  <Package className="h-8 w-8 text-emerald-500 opacity-50" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {productLineAnalytics?.total_contacts > 0 
                    ? `${Math.round((productLineAnalytics?.contacts_with_product_line / productLineAnalytics?.total_contacts) * 100)}% of contacts`
                    : '0%'}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Without Product Line</p>
                    <p className="text-2xl font-bold">{productLineAnalytics?.contacts_without_product_line || 0}</p>
                  </div>
                  <Users className="h-8 w-8 text-slate-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Multi-Product Customers</p>
                    <p className="text-2xl font-bold">{productLineAnalytics?.multi_product_line_contacts || 0}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-purple-500 opacity-50" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">Customers with 2+ products</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Product Line Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Product Line Distribution
                </CardTitle>
                <CardDescription>Contacts per product line</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {productLineAnalytics?.product_line_stats?.map((pl) => (
                  <div key={pl.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={pl.color}>{pl.name}</Badge>
                      </div>
                      <span className="font-medium">{pl.contact_count} contacts</span>
                    </div>
                    <Progress 
                      value={(pl.contact_count / getMaxCount()) * 100} 
                      className="h-2"
                    />
                  </div>
                ))}
                {(!productLineAnalytics?.product_line_stats || productLineAnalytics?.product_line_stats.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">No product line data available</p>
                )}
              </CardContent>
            </Card>

            {/* Top Product Combinations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Top Product Combinations
                </CardTitle>
                <CardDescription>Most common multi-product customers</CardDescription>
              </CardHeader>
              <CardContent>
                {productLineAnalytics?.top_combinations?.length > 0 ? (
                  <div className="space-y-3">
                    {productLineAnalytics.top_combinations.map((combo, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex flex-wrap gap-1">
                          {combo.product_lines.map((pl, i) => (
                            <Badge key={i} variant="outline">{pl}</Badge>
                          ))}
                        </div>
                        <Badge className="bg-purple-100 text-purple-700">{combo.count} customers</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No multi-product customers yet</p>
                    <p className="text-sm">Customers with 2+ product lines will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Monthly Trends */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Monthly New Customers by Product Line
              </CardTitle>
              <CardDescription>Last 6 months trend</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium">Month</th>
                      {productLineAnalytics?.product_line_stats?.map(pl => (
                        <th key={pl.id} className="text-center py-2">
                          <Badge className={pl.color} variant="outline">{pl.name}</Badge>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {productLineAnalytics?.monthly_trends?.map((month, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="py-2 font-medium">{month.month}</td>
                        {productLineAnalytics?.product_line_stats?.map(pl => (
                          <td key={pl.id} className="text-center py-2">
                            {month.product_lines[pl.name] || 0}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Overview Tab */}
        <TabsContent value="sales" className="space-y-6">
          {/* Sales Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Pipeline</p>
                    <p className="text-2xl font-bold">{formatCurrency(salesOverview?.total_pipeline_value || 0)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Closed Won</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatCurrency(salesOverview?.closed_won_value || 0)}</p>
                  </div>
                  <ArrowUpRight className="h-8 w-8 text-emerald-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Win Rate</p>
                    <p className="text-2xl font-bold">{salesOverview?.win_rate || 0}%</p>
                  </div>
                  <Target className="h-8 w-8 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Deals</p>
                    <p className="text-2xl font-bold">{salesOverview?.total_deals || 0}</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-amber-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Deal Pipeline */}
          <Card>
            <CardHeader>
              <CardTitle>Deal Pipeline by Stage</CardTitle>
              <CardDescription>Distribution of deals across pipeline stages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {salesOverview?.deal_by_stage && Object.entries(salesOverview.deal_by_stage).map(([stage, data]) => {
                  const stageLabels = {
                    prospecting: { label: 'Prospecting', color: 'bg-slate-100' },
                    qualification: { label: 'Qualification', color: 'bg-blue-100' },
                    proposal: { label: 'Proposal', color: 'bg-purple-100' },
                    negotiation: { label: 'Negotiation', color: 'bg-amber-100' },
                    closed_won: { label: 'Won', color: 'bg-emerald-100' },
                    closed_lost: { label: 'Lost', color: 'bg-red-100' }
                  };
                  const info = stageLabels[stage] || { label: stage, color: 'bg-slate-100' };
                  
                  return (
                    <Card key={stage} className={info.color}>
                      <CardContent className="pt-4 text-center">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{info.label}</p>
                        <p className="text-2xl font-bold mt-1">{data.count}</p>
                        <p className="text-sm text-muted-foreground">{formatCurrency(data.value)}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
